import { ShipmentInfo } from "@/types";

/** Claves de localStorage de la época de "un viaje por día". Se borran al hidratar. */
export const LEGACY_SHIPMENT_KEYS = [
  "todayShipment",
  "tomorrowShipment",
  "shipmentIdforToday",
  "shipmentStatus",
] as const;

const shiftRank = (shift: string): number => {
  const idx = ["AM", "PM"].indexOf(shift);
  return idx === -1 ? 2 : idx;
};

type RawShipment = {
  id: number | string;
  date: string;
  shift?: string | null;
  time?: string | null;
  status: string;
  stops?: unknown[] | null;
  routeId?: string | null;
  routeSubId?: string | null;
  svc?: {
    id?: number | null;
    code?: string | null;
    name?: string | null;
    address?: string | null;
    operationId?: number | null;
  } | null;
};

/**
 * Mapea la respuesta cruda de /me/shipments a ShipmentInfo y la ordena por
 * turno (AM, PM, otros) e id. El back ya ordena así; se repite acá para que la
 * pantalla no dependa del orden del cache ni de una respuesta vieja.
 */
export const toShipmentInfos = (rows: unknown[]): ShipmentInfo[] => {
  if (!Array.isArray(rows)) return [];
  return (rows as RawShipment[])
    .map((s) => ({
      id: String(s.id),
      date: s.date,
      svc: s.svc?.name || s.svc?.code || "N/A",
      svcId: s.svc?.id ?? null,
      operationId: s.svc?.operationId ?? null,
      shift: s.shift || "N/A",
      time: s.time || "N/A",
      address: s.svc?.address || "N/A",
      status: s.status,
      stops: (s.stops as ShipmentInfo["stops"]) ?? null,
      routeId: s.routeId ?? null,
      routeSubId: s.routeSubId ?? null,
    }))
    .sort((a, b) => {
      const byShift = shiftRank(a.shift) - shiftRank(b.shift);
      if (byShift !== 0) return byShift;
      return Number(a.id) - Number(b.id);
    });
};

/**
 * Lee un array de ShipmentInfo del cache. Cualquier cosa que no sea un array
 * (la forma vieja era un objeto suelto) se descarta y se borra la clave, para
 * que el siguiente fetch la reescriba bien.
 */
export const readCachedShipments = (key: string): ShipmentInfo[] | null => {
  const saved = localStorage.getItem(key);
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed as ShipmentInfo[];
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};
